import { Component, HostListener, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Observable, of, throwError } from 'rxjs';
import { catchError, finalize, map, switchMap, tap } from 'rxjs/operators';
import {
  ControlDpDigitalAdmissionVerification,
  ControlDpEmployeeDocumentFile,
  ControlDpEmployeeDocumentsResponse,
  ControlDpService
} from '../../services/controldp.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDatepicker, MatDatepickerInputEvent } from '@angular/material/datepicker';
import { MatSelect } from '@angular/material/select';

type DigitalAdmissionStage = 'pin' | 'form' | 'documents';
type DocumentCategoryKey = 'RG_CPF' | 'ADDRESS' | 'BANK' | 'OTHERS';
type SubmissionFeedbackState = 'idle' | 'loading' | 'success' | 'error';

@Component({
  selector: 'app-digital-admission',
  templateUrl: './digital-admission.component.html',
  styleUrls: ['./digital-admission.component.scss']
})
export class DigitalAdmissionComponent implements OnInit, OnDestroy {
  pinForm: FormGroup;
  detailsForm: FormGroup;
  stage: DigitalAdmissionStage = 'pin';
  admissions?: ControlDpDigitalAdmissionVerification;
  token?: string;
  pinValue = '';
  isSaving = false;
  errorMessage?: string;
  currentDateLabel = '';
  currentTimeLabel = '';
  isMobileSummaryOpen = false;
  selectedPhotoFile: File | null = null;
  photoPreviewUrl: string | null = null;
  isPhotoDragging = false;
  removePhotoOnSave = false;
  isLoadingDocuments = false;
  documentsLoaded = false;
  documentsError?: string;
  uploadingCategory: DocumentCategoryKey | null = null;
  deletingFileId: number | null = null;
  draggingCategory: DocumentCategoryKey | null = null;
  previewFile: ControlDpEmployeeDocumentFile | null = null;
  showReviewModal = false;
  submissionFeedbackState: SubmissionFeedbackState = 'idle';
  submissionFeedbackMessage = '';
  documentCategories: Array<{ key: DocumentCategoryKey; label: string; helper: string }> = [
    { key: 'RG_CPF', label: 'RG e CPF', helper: 'Documento de identificação oficial.' },
    { key: 'ADDRESS', label: 'Comprovante de endereço', helper: 'Conta ou correspondência atualizada.' },
    { key: 'BANK', label: 'Dados bancários', helper: 'Comprovante de conta para pagamento.' },
    { key: 'OTHERS', label: 'Outros', helper: 'Arquivos complementares necessários.' }
  ];
  requiredDocumentCategories: DocumentCategoryKey[] = ['RG_CPF', 'ADDRESS', 'BANK'];
  documentsByCategory: Record<DocumentCategoryKey, ControlDpEmployeeDocumentFile[]> = this.createEmptyDocumentsByCategory();
  genderOptions = [
    { value: 'NOT_INFORMED', label: 'Prefiro não informar' },
    { value: 'F', label: 'Feminino' },
    { value: 'M', label: 'Masculino' },
    { value: 'OTHER', label: 'Outro' }
  ];
  maritalStatusOptions = [
    'Solteiro(a)',
    'Casado(a)',
    'União estável',
    'Divorciado(a)',
    'Viúvo(a)',
    'Separado(a)'
  ];
  educationLevelOptions = [
    'Ensino fundamental incompleto',
    'Ensino fundamental completo',
    'Ensino médio incompleto',
    'Ensino médio completo',
    'Ensino superior incompleto',
    'Ensino superior completo',
    'Pós-graduação',
    'Mestrado',
    'Doutorado'
  ];
  raceOptions = ['Branca', 'Preta', 'Parda', 'Amarela', 'Indígena', 'Não informado'];
  birthLocationOptions = [
    'Acre - AC',
    'Alagoas - AL',
    'Amapá - AP',
    'Amazonas - AM',
    'Bahia - BA',
    'Ceará - CE',
    'Distrito Federal - DF',
    'Espírito Santo - ES',
    'Goiás - GO',
    'Maranhão - MA',
    'Mato Grosso - MT',
    'Mato Grosso do Sul - MS',
    'Minas Gerais - MG',
    'Pará - PA',
    'Paraíba - PB',
    'Paraná - PR',
    'Pernambuco - PE',
    'Piauí - PI',
    'Rio de Janeiro - RJ',
    'Rio Grande do Norte - RN',
    'Rio Grande do Sul - RS',
    'Rondônia - RO',
    'Roraima - RR',
    'Santa Catarina - SC',
    'São Paulo - SP',
    'Sergipe - SE',
    'Tocantins - TO',
    'Exterior'
  ];
  private clockIntervalId: number | null = null;
  private photoPreviewObjectUrl: string | null = null;
  private cepLookupInProgress = false;
  private lastCepLookup = '';
  @ViewChild('birthDatePicker') birthDatePicker?: MatDatepicker<Date>;
  @ViewChildren(MatSelect) matSelectComponents?: QueryList<MatSelect>;
  private readonly dateTimeFormatter = new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
  private readonly headerDateFormatter = new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric'
  });
  private readonly headerTimeFormatter = new Intl.DateTimeFormat('pt-BR', {
    hour: '2-digit',
    minute: '2-digit'
  });
  private readonly requiredFormFields = [
    'rg',
    'birth_date',
    'birth_location',
    'race',
    'gender',
    'marital_status',
    'mother_name',
    'education_level',
    'phone',
    'emergency_contact_name',
    'emergency_contact_phone',
    'cep',
    'street',
    'number',
    'neighborhood',
    'city',
    'state'
  ] as const;

  constructor(
    private fb: FormBuilder,
    private controldpService: ControlDpService,
    private snackBar: MatSnackBar
  ) {
    const search = new URLSearchParams(window.location.search);
    this.token = search.get('token') || undefined;
    this.pinForm = this.fb.group({ pin: ['', [Validators.required, Validators.pattern(/^\d{6}$/)]] });
    this.detailsForm = this.fb.group({
      rg: [''],
      birth_date: [''],
      birth_location: [''],
      race: [''],
      gender: ['NOT_INFORMED'],
      marital_status: [''],
      mother_name: [''],
      father_name: [''],
      education_level: [''],
      phone: [''],
      emergency_contact_name: [''],
      emergency_contact_phone: [''],
      cep: [''],
      street: [''],
      number: [''],
      complement: [''],
      neighborhood: [''],
      city: [''],
      state: [''],
      notes: ['']
    });
  }

  ngOnInit() {
    this.refreshHeaderClock();
    this.clockIntervalId = window.setInterval(() => this.refreshHeaderClock(), 60000);
  }

  ngOnDestroy() {
    if (this.clockIntervalId !== null) {
      window.clearInterval(this.clockIntervalId);
    }
    this.cleanupPhotoPreviewObjectUrl();
  }

  submitPin() {
    if (!this.pinForm.valid || !this.token) {
      return;
    }
    const pin = String(this.pinForm.get('pin')?.value || '').trim();
    this.controldpService.verifyDigitalAdmission(this.token, pin).subscribe({
      next: (verification: ControlDpDigitalAdmissionVerification) => {
        this.admissions = verification;
        this.pinValue = pin;
        this.detailsForm.patchValue(this.normalizeIncomingFields(verification.fields));
        this.applyFormReadOnlyState();
        this.stage = 'form';
        this.resetDocumentsState();
        this.loadDocuments(true);
        this.errorMessage = undefined;
      },
      error: (error: any) => {
        this.errorMessage = error?.error?.error || 'PIN incorreto.';
        this.snackBar.open(this.errorMessage || 'PIN incorreto.', 'Fechar', { duration: 4000 });
      }
    });
  }

  saveDetails(openDocumentsAfterSave = false) {
    if (!this.admissions || this.detailsForm.invalid || this.isAdmissionUnderReview) {
      return;
    }
    this.isSaving = true;
    this.persistDetails$({ showSuccessSnack: !openDocumentsAfterSave })
      .pipe(finalize(() => (this.isSaving = false)))
      .subscribe({
        next: () => {
          if (openDocumentsAfterSave) {
            this.snackBar.open('Dados salvos. Vamos para a etapa de documentos.', 'Fechar', { duration: 3500 });
            this.goToDocumentsStage();
          }
        },
        error: () => {}
      });
  }

  get completionPercentage(): number {
    const filled = this.requiredFormFields.filter((field) => this.isRequiredFieldFilled(field)).length;
    return Math.round((filled / this.requiredFormFields.length) * 100);
  }

  get activeProgressPercentage(): number {
    const totalRequiredItems = this.requiredFormFields.length + this.requiredDocumentCategories.length;
    const filledRequiredItems = this.requiredFormFilledCount + this.requiredDocumentsFilledCount;
    return totalRequiredItems > 0 ? Math.round((filledRequiredItems / totalRequiredItems) * 100) : 0;
  }

  get documentsCompletionPercentage(): number {
    const total = this.documentCategories.length;
    const filled = this.documentCategories.filter((category) => this.documentsByCategory[category.key].length > 0).length;
    return total > 0 ? Math.round((filled / total) * 100) : 0;
  }

  get totalUploadedDocuments(): number {
    return this.documentCategories.reduce((acc, category) => acc + this.documentsByCategory[category.key].length, 0);
  }

  get requiredDocumentsFilledCount(): number {
    return this.requiredDocumentCategories.filter((category) => this.documentsByCategory[category].length > 0).length;
  }

  get requiredFormFilledCount(): number {
    return this.requiredFormFields.filter((field) => this.isRequiredFieldFilled(field)).length;
  }

  get isAdmissionReadyForSubmit(): boolean {
    return this.requiredFormFilledCount === this.requiredFormFields.length
      && this.requiredDocumentsFilledCount === this.requiredDocumentCategories.length;
  }

  get isAdmissionUnderReview(): boolean {
    return this.normalizeStatus(this.admissions?.header?.status) === 'ADMISSION_COMPLETED';
  }

  saveAndContinueToDocuments() {
    this.saveDetails(true);
  }

  openReviewModal() {
    if (!this.admissions) {
      return;
    }
    this.showReviewModal = true;
    if (!this.documentsLoaded && this.token && this.pinValue) {
      this.loadDocuments(true);
    }
  }

  closeReviewModal() {
    if (this.submissionFeedbackState === 'loading') {
      return;
    }
    this.showReviewModal = false;
  }

  submitAdmissionForReview() {
    if (!this.admissions || !this.token || !this.pinValue || this.submissionFeedbackState === 'loading') {
      return;
    }
    if (!this.isAdmissionReadyForSubmit) {
      this.snackBar.open('Preencha os campos obrigatórios e envie RG/CPF, comprovante de endereço e dados bancários.', 'Fechar', {
        duration: 4500
      });
      return;
    }

    this.submissionFeedbackState = 'loading';
    this.submissionFeedbackMessage = '';
    this.isSaving = true;
    this.persistDetails$({ showSuccessSnack: false })
      .pipe(
        switchMap(() => this.controldpService.submitDigitalAdmission(this.token!, this.pinValue)),
        finalize(() => {
          this.isSaving = false;
        })
      )
      .subscribe({
        next: (response) => {
          if (this.admissions) {
            this.admissions.header.status = response?.status || 'ADMISSION_COMPLETED';
          }
          this.applyFormReadOnlyState();
          this.showReviewModal = false;
          this.submissionFeedbackState = 'success';
          this.snackBar.open('Dados enviados com sucesso! Status atualizado para aguardando análise.', 'Fechar', { duration: 4500 });
          window.setTimeout(() => {
            this.submissionFeedbackState = 'idle';
          }, 1800);
        },
        error: (error: any) => {
          if (this.isAlreadySubmittedConflict(error)) {
            if (this.admissions) {
              this.admissions.header.status = 'ADMISSION_COMPLETED';
            }
            this.applyFormReadOnlyState();
            this.showReviewModal = false;
            this.submissionFeedbackState = 'success';
            this.submissionFeedbackMessage = '';
            this.snackBar.open('Cadastro já enviado e aguardando análise.', 'Fechar', { duration: 4500 });
            window.setTimeout(() => {
              this.submissionFeedbackState = 'idle';
            }, 1800);
            return;
          }
          this.submissionFeedbackState = 'error';
          this.submissionFeedbackMessage = error?.error?.error || 'Não foi possível enviar o cadastro para análise.';
        }
      });
  }

  dismissSubmissionFeedback() {
    if (this.submissionFeedbackState === 'loading') {
      return;
    }
    this.submissionFeedbackState = 'idle';
    this.submissionFeedbackMessage = '';
  }

  toggleMobileSummary() {
    this.isMobileSummaryOpen = !this.isMobileSummaryOpen;
  }

  goToFormStage() {
    if (!this.admissions) {
      return;
    }
    this.stage = 'form';
  }

  goToDocumentsStage() {
    if (!this.admissions || !this.token || !this.pinValue) {
      this.snackBar.open('Valide o PIN para acessar a etapa de documentos.', 'Fechar', { duration: 3500 });
      return;
    }
    this.stage = 'documents';
    this.loadDocuments();
  }

  @HostListener('document:click', ['$event'])
  onDocumentMouseDown(event: MouseEvent) {
    if (this.stage === 'pin') {
      return;
    }
    const target = event.target as HTMLElement | null;
    if (!target) {
      return;
    }

    const clickedInsideDateField = !!(
      target.closest('.digital-admission-date-field')
      || target.closest('.mat-mdc-datepicker-content')
      || target.closest('.mat-datepicker-content')
      || target.closest('.mat-datepicker-toggle')
      || target.closest('.mat-calendar')
      || target.closest('.mat-calendar-body')
      || target.closest('.mat-calendar-body-cell')
    );

    if (!clickedInsideDateField) {
      this.birthDatePicker?.close();
    }

    const clickedInsideSelect = !!(
      target.closest('.mat-mdc-select-trigger')
      || target.closest('.mat-mdc-select-panel')
      || target.closest('.mat-mdc-option')
      || target.closest('.mdc-list-item')
    );

    if (!clickedInsideSelect) {
      this.closeAllSelects();
    }
  }

  onDateInputMask(controlName: 'birth_date', event: Event) {
    const input = event.target as HTMLInputElement | null;
    if (!input) {
      return;
    }
    const digits = String(input.value || '').replace(/\D/g, '').slice(0, 8);
    let masked = digits;
    if (digits.length > 4) {
      masked = `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
    } else if (digits.length > 2) {
      masked = `${digits.slice(0, 2)}/${digits.slice(2)}`;
    }
    if (input.value !== masked) {
      input.value = masked;
    }
    this.detailsForm.get(controlName)?.setValue(masked, { emitEvent: false });
  }

  onDateInputBlur(controlName: 'birth_date') {
    const control = this.detailsForm.get(controlName);
    if (!control) {
      return;
    }
    const parsed = this.parseDateOnly(control.value);
    control.setValue(parsed ? this.formatDateToBr(parsed) : String(control.value || '').trim(), { emitEvent: false });
  }

  onDatepickerChange(controlName: 'birth_date', event: MatDatepickerInputEvent<Date>) {
    const parsed = this.parseDateOnly(event?.value);
    this.detailsForm.get(controlName)?.setValue(parsed ? this.formatDateToBr(parsed) : '', { emitEvent: false });
    this.birthDatePicker?.close();
  }

  loadDocuments(force = false) {
    if (!this.token || !this.pinValue) {
      return;
    }
    if (this.isLoadingDocuments) {
      return;
    }
    if (this.documentsLoaded && !force) {
      return;
    }
    this.isLoadingDocuments = true;
    this.documentsError = undefined;
    this.controldpService
      .getDigitalAdmissionDocuments(this.token, this.pinValue)
      .pipe(finalize(() => (this.isLoadingDocuments = false)))
      .subscribe({
        next: (response: ControlDpEmployeeDocumentsResponse) => {
          this.applyDocumentsResponse(response);
          this.documentsLoaded = true;
        },
        error: (error: any) => {
          this.documentsError = error?.error?.error || 'Não foi possível carregar os documentos.';
        }
      });
  }

  openDocumentPicker(input: HTMLInputElement) {
    if (this.isAdmissionUnderReview) {
      return;
    }
    input.click();
  }

  onDocumentSelected(event: Event, category: DocumentCategoryKey) {
    if (this.isAdmissionUnderReview) {
      return;
    }
    const target = event.target as HTMLInputElement;
    const file = target.files?.[0];
    target.value = '';
    if (!file) {
      return;
    }
    this.uploadDocument(category, file);
  }

  onDocumentDragOver(event: DragEvent, category: DocumentCategoryKey) {
    event.preventDefault();
    if (this.uploadingCategory || this.isAdmissionUnderReview) {
      return;
    }
    this.draggingCategory = category;
  }

  onDocumentDragLeave(event: DragEvent, category: DocumentCategoryKey) {
    event.preventDefault();
    if (this.draggingCategory !== category) {
      return;
    }
    const currentTarget = event.currentTarget as HTMLElement | null;
    const related = event.relatedTarget as Node | null;
    if (!currentTarget || !related || !currentTarget.contains(related)) {
      this.draggingCategory = null;
    }
  }

  onDocumentDrop(event: DragEvent, category: DocumentCategoryKey) {
    event.preventDefault();
    this.draggingCategory = null;
    if (this.uploadingCategory || this.isAdmissionUnderReview) {
      return;
    }
    const file = event.dataTransfer?.files?.[0];
    if (!file) {
      return;
    }
    this.uploadDocument(category, file);
  }

  uploadDocument(category: DocumentCategoryKey, file: File) {
    if (!this.token || !this.pinValue || this.isAdmissionUnderReview) {
      return;
    }
    if (file.size > 30 * 1024 * 1024) {
      this.snackBar.open('O arquivo excede o limite de 30MB.', 'Fechar', { duration: 3500 });
      return;
    }
    this.uploadingCategory = category;
    this.documentsError = undefined;
    this.controldpService
      .uploadDigitalAdmissionDocument(this.token, this.pinValue, category, file)
      .pipe(finalize(() => (this.uploadingCategory = null)))
      .subscribe({
        next: (response) => {
          const normalizedCategory = this.normalizeDocumentCategory(response.category);
          if (!normalizedCategory) {
            this.loadDocuments(true);
            return;
          }
          this.documentsByCategory[normalizedCategory] = [response.file, ...this.documentsByCategory[normalizedCategory]];
          this.documentsByCategory[normalizedCategory].sort((a, b) => (b.uploaded_at || '').localeCompare(a.uploaded_at || ''));
          if (!this.previewFile) {
            this.previewFile = response.file;
          }
          this.snackBar.open('Documento enviado com sucesso.', 'Fechar', { duration: 3000 });
        },
        error: (error: any) => {
          const message = error?.error?.error || 'Não foi possível enviar o documento.';
          this.snackBar.open(message, 'Fechar', { duration: 4000 });
        }
      });
  }

  removeDocument(category: DocumentCategoryKey, file: ControlDpEmployeeDocumentFile) {
    if (!this.token || !this.pinValue || !file?.id || this.isAdmissionUnderReview) {
      return;
    }
    this.deletingFileId = file.id;
    this.documentsError = undefined;
    this.controldpService
      .deleteDigitalAdmissionDocument(this.token, this.pinValue, file.id)
      .pipe(finalize(() => (this.deletingFileId = null)))
      .subscribe({
        next: () => {
          this.documentsByCategory[category] = this.documentsByCategory[category].filter((item) => item.id !== file.id);
          if (this.previewFile?.id === file.id) {
            this.previewFile = null;
          }
          this.snackBar.open('Documento removido.', 'Fechar', { duration: 2500 });
        },
        error: (error: any) => {
          const message = error?.error?.error || 'Não foi possível remover o documento.';
          this.snackBar.open(message, 'Fechar', { duration: 4000 });
        }
      });
  }

  formatUploadedAt(value?: string | null): string {
    if (!value) {
      return '-';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return String(value);
    }
    return this.dateTimeFormatter.format(date);
  }

  formatFileSize(bytes: number): string {
    if (!Number.isFinite(bytes) || bytes <= 0) {
      return '0 B';
    }
    const units = ['B', 'KB', 'MB', 'GB'];
    let size = bytes;
    let unitIndex = 0;
    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex += 1;
    }
    const precision = unitIndex === 0 ? 0 : 1;
    return `${size.toFixed(precision)} ${units[unitIndex]}`;
  }

  trackDocumentById(_: number, file: ControlDpEmployeeDocumentFile): number {
    return file.id;
  }

  openDocumentPreview(file: ControlDpEmployeeDocumentFile) {
    this.previewFile = file;
  }

  closeDocumentPreview() {
    this.previewFile = null;
  }

  isImageDocument(file?: ControlDpEmployeeDocumentFile | null): boolean {
    const type = String(file?.file_type || '').toLowerCase();
    return type.startsWith('image/');
  }

  isPdfDocument(file?: ControlDpEmployeeDocumentFile | null): boolean {
    const type = String(file?.file_type || '').toLowerCase();
    const name = String(file?.name || '').toLowerCase();
    return type.includes('pdf') || name.endsWith('.pdf');
  }

  canPreviewDocument(file?: ControlDpEmployeeDocumentFile | null): boolean {
    return this.isImageDocument(file) || this.isPdfDocument(file);
  }

  documentUrl(file?: ControlDpEmployeeDocumentFile | null): string {
    const path = String(file?.file_path || '').trim();
    if (!path) {
      return '';
    }
    return this.resolvePhotoUrl(path);
  }

  onPhoneInput(controlName: 'phone' | 'emergency_contact_phone') {
    const control = this.detailsForm.get(controlName);
    if (!control) {
      return;
    }
    const masked = this.formatPhone(control.value);
    if (masked !== control.value) {
      control.setValue(masked);
    }
  }

  onCepInput() {
    const control = this.detailsForm.get('cep');
    if (!control) {
      return;
    }
    const masked = this.formatCep(control.value);
    if (masked !== control.value) {
      control.setValue(masked);
    }
    this.lookupAddressByCepIfReady(false);
  }

  onCepBlur() {
    this.lookupAddressByCepIfReady(true);
  }

  onStateInput() {
    const control = this.detailsForm.get('state');
    if (!control) {
      return;
    }
    const normalized = this.normalizeState(control.value);
    if (normalized !== control.value) {
      control.setValue(normalized);
    }
  }

  openPhotoPicker(input: HTMLInputElement) {
    if (this.isAdmissionUnderReview) {
      return;
    }
    input.click();
  }

  onPhotoSelected(event: Event) {
    if (this.isAdmissionUnderReview) {
      return;
    }
    const target = event.target as HTMLInputElement;
    const file = target.files?.[0];
    this.handlePhotoFile(file);
    target.value = '';
  }

  onPhotoDragOver(event: DragEvent) {
    event.preventDefault();
    if (this.isAdmissionUnderReview) {
      return;
    }
    this.isPhotoDragging = true;
  }

  onPhotoDragLeave(event: DragEvent) {
    event.preventDefault();
    this.isPhotoDragging = false;
  }

  onPhotoDrop(event: DragEvent) {
    event.preventDefault();
    this.isPhotoDragging = false;
    if (this.isAdmissionUnderReview) {
      return;
    }
    const file = event.dataTransfer?.files?.[0];
    this.handlePhotoFile(file);
  }

  removePhoto() {
    if (this.isAdmissionUnderReview) {
      return;
    }
    this.selectedPhotoFile = null;
    this.setPhotoPreviewFromUrl(null);
    this.removePhotoOnSave = true;
  }

  formatDateTime(value?: string | null): string {
    if (!value) {
      return '-';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }
    return this.dateTimeFormatter.format(date);
  }

  initialsFromName(name?: string | null): string {
    const source = (name || '').trim();
    if (!source) {
      return 'U';
    }
    return source
      .split(' ')
      .filter((part) => part.trim().length)
      .slice(0, 2)
      .map((part) => part[0])
      .join('')
      .toUpperCase();
  }

  admissionStatusLabel(status?: string | null): string {
    const statusValue = this.normalizeStatus(status);
    if (statusValue === 'ADMISSION_COMPLETED') {
      return 'Aguardando análise';
    }
    if (statusValue === 'PENDING_ADMISSION') {
      return 'Aguardando preenchimento';
    }
    if (statusValue === 'ADMISSION_CANCELED') {
      return 'Admissão cancelada';
    }
    if (statusValue === 'EXPIRED') {
      return 'Prazo expirado';
    }
    return 'Aguardando preenchimento';
  }

  reviewStatusLabel(): string {
    if (this.isAdmissionUnderReview) {
      return 'Enviado para análise';
    }
    if (this.isAdmissionReadyForSubmit) {
      return 'Pronto para envio';
    }
    return 'Pendente de preenchimento';
  }

  displayDetailValue(controlName: string): string {
    const raw = this.detailsForm.get(controlName)?.value;
    const text = String(raw ?? '').trim();
    return text || '-';
  }

  stageLabel(stage: 'form' | 'documents'): string {
    return stage === 'documents' ? '2. Documentos' : '1. Dados pessoais';
  }

  stageTitle(): string {
    return this.stage === 'documents' ? 'Envio de documentos' : 'Cadastro de dados pessoais';
  }

  stageDescription(): string {
    if (this.stage === 'documents') {
      return 'Envie seus documentos por categoria. Esses arquivos ficam visíveis apenas na admissão digital e no cadastro do colaborador.';
    }
    return 'Revise os dados do convite e complete os campos abaixo para concluir sua admissão.';
  }

  private persistDetails$(options?: { showSuccessSnack?: boolean }): Observable<void> {
    if (!this.admissions) {
      return throwError(() => new Error('Admissão não carregada.'));
    }
    const values = this.detailsForm.value;
    const basePayload: any = {
      tenant_id: this.admissions.header.tenant_id,
      ...values,
      birth_date: this.normalizeDateForPayload(values.birth_date),
      phone: this.formatPhone(values.phone),
      emergency_contact_phone: this.formatPhone(values.emergency_contact_phone),
      cep: this.formatCep(values.cep),
      state: this.normalizeState(values.state)
    };
    const upload$ = this.selectedPhotoFile
      ? this.controldpService
          .uploadAttachment(this.admissions.header.tenant_id, this.admissions.employee_id, 0, this.selectedPhotoFile)
          .pipe(map((attachment) => String(attachment?.file_path || '')))
      : of('');

    return upload$.pipe(
      switchMap((photoPath) => {
        const payload: any = { ...basePayload };
        const nextPhotoUrl = photoPath || (this.removePhotoOnSave ? null : undefined);
        if (nextPhotoUrl !== undefined) {
          payload.photo_url = nextPhotoUrl;
        }
        return this.controldpService.updateEmployee(this.admissions!.employee_id, payload).pipe(
          map(() => nextPhotoUrl)
        );
      }),
      tap((nextPhotoUrl) => {
        if (nextPhotoUrl !== undefined) {
          this.setPhotoPreviewFromUrl(nextPhotoUrl);
          this.selectedPhotoFile = null;
          this.removePhotoOnSave = false;
        }
      }),
      tap(() => {
        if (options?.showSuccessSnack !== false) {
          this.snackBar.open('Dados salvos com sucesso. Aguarde a próxima etapa do DP.', 'Fechar', { duration: 4000 });
        }
      }),
      map(() => void 0),
      catchError((error: any) => {
        const message = error?.error?.error || 'Não foi possível gravar os dados.';
        this.snackBar.open(message, 'Fechar', { duration: 4000 });
        return throwError(() => error);
      })
    );
  }

  private applyFormReadOnlyState() {
    if (this.isAdmissionUnderReview) {
      this.detailsForm.disable({ emitEvent: false });
      return;
    }
    this.detailsForm.enable({ emitEvent: false });
  }

  private refreshHeaderClock() {
    const now = new Date();
    this.currentDateLabel = this.headerDateFormatter.format(now);
    this.currentTimeLabel = this.headerTimeFormatter.format(now);
  }

  private applyDocumentsResponse(response: ControlDpEmployeeDocumentsResponse) {
    this.documentsByCategory = {
      RG_CPF: [...(response?.files?.RG_CPF || [])],
      ADDRESS: [...(response?.files?.ADDRESS || [])],
      BANK: [...(response?.files?.BANK || [])],
      OTHERS: [...(response?.files?.OTHERS || [])]
    };
    (Object.keys(this.documentsByCategory) as DocumentCategoryKey[]).forEach((category) => {
      this.documentsByCategory[category].sort((a, b) => (b.uploaded_at || '').localeCompare(a.uploaded_at || ''));
    });
  }

  private resetDocumentsState() {
    this.documentsLoaded = false;
    this.isLoadingDocuments = false;
    this.documentsError = undefined;
    this.uploadingCategory = null;
    this.deletingFileId = null;
    this.draggingCategory = null;
    this.previewFile = null;
    this.documentsByCategory = this.createEmptyDocumentsByCategory();
  }

  private normalizeIncomingFields(fields: ControlDpDigitalAdmissionVerification['fields']) {
    const rg = String(fields.rg || '').trim();
    const birthDate = this.normalizeDateForInput(fields.birth_date);
    const gender = this.normalizeGender(fields.gender);
    const race = String(fields.race || '').trim();
    const maritalStatus = String(fields.marital_status || '').trim();
    const educationLevel = String(fields.education_level || '').trim();
    const birthLocation = String(fields.birth_location || '').trim();

    this.appendIfMissing(this.raceOptions, race);
    this.appendIfMissing(this.maritalStatusOptions, maritalStatus);
    this.appendIfMissing(this.educationLevelOptions, educationLevel);
    this.appendIfMissing(this.birthLocationOptions, birthLocation);
    this.setPhotoPreviewFromUrl(fields.photo_url || null);
    this.removePhotoOnSave = false;

    return {
      ...fields,
      rg,
      birth_date: birthDate,
      gender,
      race,
      marital_status: maritalStatus,
      education_level: educationLevel,
      birth_location: birthLocation,
      phone: this.formatPhone(fields.phone),
      emergency_contact_phone: this.formatPhone(fields.emergency_contact_phone),
      cep: this.formatCep(fields.cep),
      state: this.normalizeState(fields.state)
    };
  }

  private normalizeDateForInput(value?: string | null): string {
    const parsed = this.parseDateOnly(value);
    return parsed ? this.formatDateToBr(parsed) : '';
  }

  private normalizeDateForPayload(value?: string | null): string | null {
    const parsed = this.parseDateOnly(value);
    if (!parsed) {
      return null;
    }
    const year = parsed.getFullYear();
    const month = `${parsed.getMonth() + 1}`.padStart(2, '0');
    const day = `${parsed.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private normalizeGender(value?: string | null): string {
    const raw = String(value || '').trim().toUpperCase();
    if (['F', 'M', 'OTHER', 'NOT_INFORMED'].includes(raw)) {
      return raw;
    }
    if (['FEMININO', 'FEMALE'].includes(raw)) {
      return 'F';
    }
    if (['MASCULINO', 'MALE'].includes(raw)) {
      return 'M';
    }
    return 'NOT_INFORMED';
  }

  private normalizeState(value?: string | null): string {
    return String(value || '')
      .replace(/[^a-zA-Z]/g, '')
      .slice(0, 2)
      .toUpperCase();
  }

  private formatPhone(value?: string | null): string {
    const digits = String(value || '')
      .replace(/\D/g, '')
      .slice(0, 11);
    if (!digits) {
      return '';
    }
    if (digits.length <= 2) {
      return `(${digits}`;
    }
    if (digits.length <= 6) {
      return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
    }
    if (digits.length <= 10) {
      return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
    }
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
  }

  private formatCep(value?: string | null): string {
    const digits = String(value || '')
      .replace(/\D/g, '')
      .slice(0, 8);
    if (!digits) {
      return '';
    }
    if (digits.length <= 5) {
      return digits;
    }
    return `${digits.slice(0, 5)}-${digits.slice(5)}`;
  }

  private isRequiredFieldFilled(field: (typeof this.requiredFormFields)[number]): boolean {
    const controlValue = this.detailsForm.get(field)?.value;
    const text = String(controlValue || '').trim();
    if (field === 'gender') {
      return text.length > 0 && text !== 'NOT_INFORMED';
    }
    if (field === 'birth_date') {
      return !!this.parseDateOnly(controlValue);
    }
    if (field === 'cep') {
      return String(controlValue || '').replace(/\D/g, '').length === 8;
    }
    if (field === 'phone' || field === 'emergency_contact_phone') {
      const digits = String(controlValue || '').replace(/\D/g, '');
      return digits.length >= 10;
    }
    if (field === 'state') {
      return this.normalizeState(controlValue).length === 2;
    }
    return text.length > 0;
  }

  private lookupAddressByCepIfReady(force: boolean) {
    if (this.isAdmissionUnderReview) {
      return;
    }
    const cepValue = String(this.detailsForm.get('cep')?.value || '');
    const cepDigits = cepValue.replace(/\D/g, '');
    if (cepDigits.length !== 8) {
      if (force && cepDigits.length > 0) {
        this.snackBar.open('Informe um CEP válido com 8 dígitos.', 'Fechar', { duration: 2800 });
      }
      return;
    }
    if (!force && (this.cepLookupInProgress || this.lastCepLookup === cepDigits)) {
      return;
    }
    this.cepLookupInProgress = true;
    this.lastCepLookup = cepDigits;
    fetch(`https://viacep.com.br/ws/${cepDigits}/json/`)
      .then((response) => {
        if (!response.ok) {
          throw new Error('Falha ao consultar CEP.');
        }
        return response.json();
      })
      .then((payload: any) => {
        if (payload?.erro) {
          throw new Error('CEP não encontrado.');
        }
        const current = this.detailsForm.getRawValue();
        this.detailsForm.patchValue(
          {
            street: String(payload?.logradouro || current.street || ''),
            neighborhood: String(payload?.bairro || current.neighborhood || ''),
            city: String(payload?.localidade || current.city || ''),
            state: this.normalizeState(payload?.uf || current.state || '')
          },
          { emitEvent: false }
        );
      })
      .catch(() => {
        if (force) {
          this.snackBar.open('Não foi possível localizar o endereço pelo CEP informado.', 'Fechar', { duration: 3200 });
        }
      })
      .finally(() => {
        this.cepLookupInProgress = false;
      });
  }

  private handlePhotoFile(file?: File | null) {
    if (this.isAdmissionUnderReview) {
      return;
    }
    if (!file) {
      return;
    }
    if (!file.type.startsWith('image/')) {
      this.snackBar.open('Formato inválido. Envie uma imagem JPG ou PNG.', 'Fechar', { duration: 3500 });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      this.snackBar.open('A imagem deve ter no máximo 5MB.', 'Fechar', { duration: 3500 });
      return;
    }
    this.cleanupPhotoPreviewObjectUrl();
    this.photoPreviewObjectUrl = URL.createObjectURL(file);
    this.photoPreviewUrl = this.photoPreviewObjectUrl;
    this.selectedPhotoFile = file;
    this.removePhotoOnSave = false;
  }

  private setPhotoPreviewFromUrl(url?: string | null) {
    this.cleanupPhotoPreviewObjectUrl();
    if (!url) {
      this.photoPreviewUrl = null;
      return;
    }
    this.photoPreviewUrl = this.resolvePhotoUrl(url);
  }

  private cleanupPhotoPreviewObjectUrl() {
    if (this.photoPreviewObjectUrl) {
      URL.revokeObjectURL(this.photoPreviewObjectUrl);
      this.photoPreviewObjectUrl = null;
    }
  }

  private resolvePhotoUrl(url: string): string {
    if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('blob:') || url.startsWith('data:')) {
      return url;
    }
    return url.startsWith('/') ? url : `/${url}`;
  }

  private appendIfMissing(target: string[], value: string) {
    if (!value) {
      return;
    }
    if (!target.includes(value)) {
      target.push(value);
    }
  }

  private createEmptyDocumentsByCategory(): Record<DocumentCategoryKey, ControlDpEmployeeDocumentFile[]> {
    return {
      RG_CPF: [],
      ADDRESS: [],
      BANK: [],
      OTHERS: []
    };
  }

  private normalizeDocumentCategory(value: string): DocumentCategoryKey | null {
    const upper = String(value || '').toUpperCase();
    if (upper === 'RG_CPF' || upper === 'ADDRESS' || upper === 'BANK' || upper === 'OTHERS') {
      return upper;
    }
    return null;
  }

  private closeAllSelects() {
    this.matSelectComponents?.forEach((selectRef) => {
      if (selectRef.panelOpen) {
        selectRef.close();
      }
    });
  }

  private parseDateOnly(value: any): Date | null {
    if (value instanceof Date) {
      if (Number.isNaN(value.getTime())) {
        return null;
      }
      const clone = new Date(value);
      clone.setHours(0, 0, 0, 0);
      return clone;
    }
    const raw = String(value || '').trim();
    if (!raw) {
      return null;
    }
    const brMatch = /^(\d{2})[\/\-\.](\d{2})[\/\-\.](\d{4})$/.exec(raw);
    if (brMatch) {
      const day = Number(brMatch[1]);
      const month = Number(brMatch[2]);
      const year = Number(brMatch[3]);
      const date = new Date(year, month - 1, day);
      if (
        date.getFullYear() === year
        && date.getMonth() === month - 1
        && date.getDate() === day
      ) {
        date.setHours(0, 0, 0, 0);
        return date;
      }
      return null;
    }
    const isoMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(raw);
    if (isoMatch) {
      const year = Number(isoMatch[1]);
      const month = Number(isoMatch[2]);
      const day = Number(isoMatch[3]);
      const date = new Date(year, month - 1, day);
      if (
        date.getFullYear() === year
        && date.getMonth() === month - 1
        && date.getDate() === day
      ) {
        date.setHours(0, 0, 0, 0);
        return date;
      }
      return null;
    }
    const isoDateTimeMatch = /^(\d{4})-(\d{2})-(\d{2})(?:[T\s].+)?$/.exec(raw);
    if (isoDateTimeMatch) {
      const year = Number(isoDateTimeMatch[1]);
      const month = Number(isoDateTimeMatch[2]);
      const day = Number(isoDateTimeMatch[3]);
      const date = new Date(year, month - 1, day);
      if (
        date.getFullYear() === year
        && date.getMonth() === month - 1
        && date.getDate() === day
      ) {
        date.setHours(0, 0, 0, 0);
        return date;
      }
      return null;
    }
    const onlyDigits = raw.replace(/\D/g, '');
    if (onlyDigits.length === 8) {
      const day = Number(onlyDigits.slice(0, 2));
      const month = Number(onlyDigits.slice(2, 4));
      const year = Number(onlyDigits.slice(4, 8));
      const date = new Date(year, month - 1, day);
      if (
        date.getFullYear() === year
        && date.getMonth() === month - 1
        && date.getDate() === day
      ) {
        date.setHours(0, 0, 0, 0);
        return date;
      }
    }
    const fallback = new Date(raw);
    if (!Number.isNaN(fallback.getTime())) {
      fallback.setHours(0, 0, 0, 0);
      return fallback;
    }
    return null;
  }

  private formatDateToBr(value: Date): string {
    const day = `${value.getDate()}`.padStart(2, '0');
    const month = `${value.getMonth() + 1}`.padStart(2, '0');
    const year = value.getFullYear();
    return `${day}/${month}/${year}`;
  }

  private normalizeStatus(value?: string | null): string {
    return String(value || '').trim().toUpperCase();
  }

  private isAlreadySubmittedConflict(error: any): boolean {
    const statusCode = Number(error?.status || 0);
    if (statusCode !== 409) {
      return false;
    }
    const rawMessage = String(
      error?.error?.error
      || error?.error?.message
      || error?.message
      || ''
    ).toLowerCase();
    return rawMessage.includes('já enviado')
      || rawMessage.includes('ja enviado')
      || rawMessage.includes('aguardando análise')
      || rawMessage.includes('aguardando analise')
      || rawMessage.includes('admission_completed');
  }
}
